(ns manage.campaign.table.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium]]
            ["moment/min/moment-with-locales$default" :as moment]
            ["src/svg/inform/plus.svg$default" :as Plus]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.patterns.stats-daterange-select :refer [daterange-select
                                                            get-initial-daterange
                                                            recent-stats-daterange]]
            [common.queries :refer [use-mutation use-queries]]
            [common.resource.query.campaign :as cq]
            [common.util :refer [->locale-string format-timestamp left-join spread-obj]]
            [common.widgets.data-grid :as dg]
            [manage.adset-table.view :refer [derive-status format-status-with-tooltip
                                             status-tooltip]]
            [manage.campaign.hooks :as ch]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui new-campaign-button []
  ($ Button
     {:variant "contained"
      :onClick #(rf/dispatch [:modal/open {:id :campaign/create}])
      :startIcon ($ Plus)
      :data-testid :create-campaign-button}
     "New campaign"))

(defui create-campaign-button []
  ($ Button
     {:variant "contained"
      :onClick #(rf/dispatch [:modal/open {:id :campaign/link
                                           :props {:link-mode :create}}])
      :data-testid :link-campaign-button}
     "Create a campaign"))

(defui link-campaign-button []
  ($ Button
     {:onClick #(rf/dispatch [:modal/open {:id :campaign/link
                                           :props {:link-mode :link}}])
      :data-testid :link-campaign-button}
     "Link existing campaign"))

(defui unlink-campaign-button [{:keys [mapped-campaign project-id]}]
  (let [{patch-campaign-wpp-project :mutate} (use-mutation (cq/patch-campaign-wpp-project {:campaign-id (:id mapped-campaign)}))]
    ($ Button
       {:variant "contained"
        :onClick (fn []
                   (patch-campaign-wpp-project {:operation "remove" :WPPOSProjectId project-id}
                                               #js {:onSuccess #(.reload js/window.location)}))
        :data-testid :unlink-campaign-button}
       "Unlink campaign")))

(def Toolbar
  (uix/as-react
   (fn [{:keys [daterange set-daterange os-enabled? os-project-available? mapped-campaign project-id]}]
     ($ dg/grid-toolbar
        ($ Stack
           {:direction "row"
            :sx #js {:flex 1}}
           ($ dg/filters-button
              {:filters [{:id "status"
                          :label "Status"
                          :field "status"
                          :filter-options (->> ["archived" "active" "inactive"]
                                               (mapv #(hash-map :value % :label (str/capitalize %))))}]}))
        ($ Stack
           {:direction "row"
            :gap 1}
           ($ daterange-select {:value daterange :on-change set-daterange})
           (if (and os-enabled? os-project-available?)
             (if (some? mapped-campaign)
               ($ unlink-campaign-button {:project-id project-id :mapped-campaign mapped-campaign})
               ($ link-campaign-button))
             ($ new-campaign-button)))))))

(defui render-name-column [{:keys [campaign]}]
  (let [{:keys [name archived adset-count]} campaign]
    ($ Stack
       ($ Typography
          (cond-> {:noWrap true}
            archived (assoc :sx #js {:color "text.disabled"}))
          name)
       ($ Typography
          {:fontSize "sm"
           :sx #js {:color "grey.20"}}
          (str adset-count
               " adset" (when-not (= adset-count 1) "s"))))))

(defui render-updated-at [{:keys [updated-at]}]
  ($ Tooltip
     {:placement "top"
      :title (format-timestamp updated-at)}
     ($ Typography
        {:component "span"}
        (.fromNow (moment (* 1000 updated-at))))))

(defui render-status [{:keys [status]}]
  (when status
    (let [tooltip (status-tooltip "campaign" status)]
      ($ format-status-with-tooltip {:status status :tooltip tooltip}))))

(def ^:private columns-data
  #js [#js {:field "name"
            :headerName "Campaign name"
            :type "string"
            :minWidth 250
            :flex 3
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:name row))
            :renderCell (fn [^js params]
                          ($ render-name-column {:campaign (.-row params)}))}

       #js {:field "impressions"
            :headerName "Impressions"
            :type "number"
            :minWidth 100
            :flex 1
            :headerAlign "right"
            :align "right"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:impressions row))
            :renderCell (fn [^js params]
                          ((fnil ->locale-string 0) (.-value params)))}

       #js {:field "clicks"
            :headerName "Clicks"
            :type "number"
            :minWidth 100
            :flex 1
            :headerAlign "right"
            :align "right"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:clicks row))
            :renderCell (fn [^js params]
                          ((fnil ->locale-string 0) (.-value params)))}

       #js {:field "ctr"
            :headerName "CTR"
            :type "number"
            :minWidth 100
            :flex 1
            :headerAlign "right"
            :align "right"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:ctr row))
            :renderCell (fn [^js params]
                          (let [ctr (or (.-value params) 0)]
                            (-> (* 100 ctr)
                                (->locale-string {:minimumFractionDigits 3})
                                (str "%"))))}

       #js {:field "updated-at"
            :headerName "Last activity"
            :type "timestamp"
            :minWidth 120
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:updatedAt row))
            :renderCell (fn [^js params]
                          ($ render-updated-at {:updated-at (.-value params)}))}

       #js {:field "status"
            :headerName "Status"
            :type "singleSelect"
            :minWidth 130
            :valueOptions #js ["archived" "active" "inactive"]
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:status row))
            :renderCell (fn [^js params]
                          ($ render-status {:status (.-value params)}))}])

(defn get-recent-impressions [^js data]
  (->> data ->clj (mapv (fn [{:keys [campaign-id impressions]}]
                          {:id campaign-id
                           :impressions-recent impressions}))))

(defn enrich-status
  [{:keys [archived impressions-recent createdAt] :as campaign}]
  (assoc campaign :status (derive-status archived impressions-recent createdAt)))

(defn combine-queries-results [^js results]
  (let [results (->clj results)
        [{campaigns :data} {stats :data} {recent-stats :data}] results]
    #js {:isPending (boolean (some :isPending results))
         :data (->> (cond-> campaigns
                      (seq stats) (left-join stats :id)
                      (seq recent-stats) (left-join recent-stats :id))
                    (mapv enrich-status))}))

(defhook use-campaigns-data [{:keys [advertiser-id daterange]}]
  (let [campaings-query (cq/campaigns-query {:advertiser-id advertiser-id})
        stats-query (cq/campaigns-stats-query {:advertiser-id advertiser-id :daterange daterange})
        recent-stats-query (spread-obj (cq/campaigns-stats-query {:advertiser-id advertiser-id :daterange recent-stats-daterange})
                                       #js {:select get-recent-impressions})]
    (use-queries #js {:queries #js [campaings-query stats-query recent-stats-query]
                      :combine combine-queries-results})))

(defui campaign-datagrid []
  (let [{:keys [agency-id advertiser-id]} (use-subscribe [:url-context])
        [daterange set-daterange] (uix/use-state (get-initial-daterange))
        {:keys [data]} (use-campaigns-data {:advertiser-id advertiser-id :daterange daterange})

        get-row-id (uix/use-callback (fn [row]
                                       (:id row))
                                     [])
        page-size 10
        initial-state (uix/use-memo (fn []
                                      #js {:sorting #js {:sortModel #js [#js {:field "updated-at" :sort "desc"}]}
                                           :pagination #js {:paginationModel #js {:pageSize page-size}}
                                           :filter #js {:filterModel #js {:items #js [#js {:id "status"
                                                                                           :field "status"
                                                                                           :operator "isAnyOf"
                                                                                           :value #js ["active" "inactive"]}]}}})
                                    [page-size])

        {:keys [os-enabled? os-project-available? mapped-campaign project-id]} (ch/use-wpp-open-project)]
    ($ DataGridPremium
       {:columns columns-data

        :rows (to-array data)
        :getRowId get-row-id

        :disableRowSelectionOnClick true

        :pagination true
        :pageSizeOptions #js [page-size]

        :initialState initial-state

        :slots #js {:cell dg/WithLinkCell
                    :toolbar Toolbar}
        :slotProps #js {:baseTextField dg/grid-toolbar-outlined-input
                        :cell #js {:href-fn (fn [row]
                                              (rfe/href :campaign/page {:agency-id agency-id
                                                                        :advertiser-id advertiser-id
                                                                        :campaign-id (:id row)}))}
                        :toolbar #js {:daterange daterange
                                      :set-daterange set-daterange
                                      :os-enabled? os-enabled?
                                      :os-project-available? os-project-available?
                                      :mapped-campaign mapped-campaign
                                      :project-id project-id}}
        :data-testid "table-campaign"

        :noWrap true
        :sx #js {:maxHeight "70rem"
                 ".greyed-out" #js {:opacity 0.45
                                    :cursor "not-allowed"}}})))
